import { MESSAGE_TYPES, type ChatMessage } from '../../types/messages';
import { PROPOSAL_BUNDLE_STATE, type ProposalBundle } from '../../types/proposalBundles';

const hasForeignRoundTripCurrentProposal = (proposalBundles: ProposalBundle[]): boolean =>
  proposalBundles.some(bundle => bundle.hasForeignFlightRoundTrip === true);

const filterProposalsByCurrentProposal = (
  messages: ChatMessage[],
  proposalBundles: ProposalBundle[]
): ChatMessage[] => {
  const activeBundleIds = new Set(proposalBundles.map(bundle => bundle.id));

  return messages.filter(message => {
    if (message.type !== MESSAGE_TYPES.PROPOSAL) {
      return true;
    }

    const ids = message.proposalBundleIds;
    if (ids == null || ids.length === 0) {
      return false;
    }

    return ids.some(id => activeBundleIds.has(id));
  });
};

const filterProposalsToLatestOnly = (messages: ChatMessage[]): ChatMessage[] => {
  const latestProposalId = messages.filter(message => message.type === MESSAGE_TYPES.PROPOSAL).slice(-1)[0]?.id;

  return messages.filter(message => {
    if (message.type === MESSAGE_TYPES.PROPOSAL && message.id !== latestProposalId) {
      return false;
    }

    return true;
  });
};

const filterReservationInfoForms = (messages: ChatMessage[], proposalBundles: ProposalBundle[]): ChatMessage[] => {
  const hasSuggestingBundles = proposalBundles.some(bundle => bundle.state === PROPOSAL_BUNDLE_STATE.SUGGESTING);
  const lastProposalIndex = messages.reduce(
    (lastIndex, message, index) => (message.type === MESSAGE_TYPES.PROPOSAL ? index : lastIndex),
    -1
  );
  const latestReservationInfoFormMessageId = messages.reduce<string | undefined>((latestId, message, index) => {
    if (message.type !== MESSAGE_TYPES.RESERVATION_INFO_FORM) {
      return latestId;
    }
    if (lastProposalIndex !== -1 && index <= lastProposalIndex) {
      return latestId;
    }
    return message.id;
  }, undefined);

  return messages.filter((message, index) => {
    if (message.type !== MESSAGE_TYPES.RESERVATION_INFO_FORM) {
      return true;
    }

    if (hasSuggestingBundles) {
      return false;
    }
    if (lastProposalIndex !== -1 && index <= lastProposalIndex) {
      return false;
    }
    return message.id === latestReservationInfoFormMessageId;
  });
};

export const filterMessagesForDisplay = (
  messages: ChatMessage[],
  proposalBundles: ProposalBundle[] = []
): ChatMessage[] => {
  const proposalFilteredMessages = hasForeignRoundTripCurrentProposal(proposalBundles)
    ? filterProposalsByCurrentProposal(messages, proposalBundles)
    : filterProposalsToLatestOnly(messages);

  return filterReservationInfoForms(proposalFilteredMessages, proposalBundles);
};
